import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { environment } from 'src/environments/environment';
import { AsgardFeatureNames } from '../../../models/AsgardFeatures';
import { BehaviorSubject, Observable } from 'rxjs';
import { ModalWindowService } from '../ui/modal-window.service';
import { LocalStorage } from '../auth/local.storage';
import { CommonMessageDialogComponent } from 'src/app/components/dialogs/common-message-dialog/common-message-dialog.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { documentElement, getFroalaIframes, resetCursorIcon, updateCursorIcon } from '../../core/common';
import { TabService } from '../tab.service';
import { CustomDocviewerCursorStyles } from '../../core/pdf-utils/enums';
import { AsgardTab } from '../../core/asgard-tab';
import { SettingsService } from '../settings/settings.service';
import { AudioSettingsV2, ISettings } from 'src/app/core/interfaces';
import { BaseApiService } from '../auth/base-api.service';
import { RequestTypeEnum } from 'src/app/utils/enums';
import { SpeechEngineVersions } from 'src/app/core/constants';
import { currentSpeechEngine } from 'src/app/core/speech-engine';

const AUDIOMAKER_USER_READ_INSTRUCTIONS_KEY = 'AUDIOMAKER_USER_READ_INSTRUCTIONS';
const AUDIOMAKER_CURSOR_LOC = 'cursor: url(\'../../../assets/images/audiomakercursor.svg\') 2 12, auto !important;';


export enum AudioMakerError {
  aboveNephilimLimit = 'ABOVENEPHILIMLIMIT',
  audioBlobIsInvalid = 'AUDIOBLOBISINVALID',
  emptyText = 'EMPTYTEXT'
}

enum AudioFormat {
  mp3 = 'mp3',
  wav = 'wav'
}

enum CursorAction {
  update = 'update',
  reset = 'reset'
}

interface IAudioMakerParameters {
  text: string;
  voice: string;
  format: AudioFormat;
  pause: number;
  rate: number;
  volume: number;
  bitRate: number;
}

@Injectable({
  providedIn: 'root'
})

export class AudioMakerService extends BaseApiService {
  private active: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly nephilimLimit: number = 3000;
  private audioFormat: AudioFormat = AudioFormat.mp3; // can later be a setting
  private currentCharacterCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private speed: number = 0;

  private get userReadInstructions(): boolean {
    const userReadInstructions: boolean = this.localStorage.get(AUDIOMAKER_USER_READ_INSTRUCTIONS_KEY) || false;
    if (userReadInstructions) {
      return userReadInstructions;
    }
    return false;
  }

  private set userReadInstructions(value: boolean) {
    this.localStorage.set<boolean>(AUDIOMAKER_USER_READ_INSTRUCTIONS_KEY, value);
  }

  constructor(
    private readonly uiStore: CurrentUiStoreService,
    public readonly http: HttpClient,
    private readonly modalWindowService: ModalWindowService,
    private readonly localStorage: LocalStorage,
    private readonly tabService: TabService,
    private readonly settigsService: SettingsService
  ) {
    super(http);

    this.uiStore.getUIState().subscribe(e => {
      if (e) {
        if (e[AsgardFeatureNames.AUDIO_MAKER].active !== this.isActive()) {
          this.active.next(e[AsgardFeatureNames.AUDIO_MAKER].active);
          if (this.isActive()) {
            this.showInstructionsIfNeeded();
          }
        }
      }
    });
    this.subToTabChange();
  }

  private static isEmpty(text: string): boolean {
    return text === '';
  }

  private static isValid(object: any): boolean {
    return !(object === undefined || object === null);
  }

  private static getMimeTypeByFormat(format: AudioFormat): string {
    switch (format) {
      case AudioFormat.mp3:
        return 'audio/mpeg';
      case AudioFormat.wav:
        return 'audio/x-wav';
      default:
        return '';
    }
  }

  static getExtensionByType(type: string): string {
    switch (type) {
      case 'audio/mpeg':
        return '.mp3';
      case 'audio/x-wav':
        return '.wav';
      default:
        return '';
    }
  }

  isActive(): boolean {
    return this.active.value;
  }

  getActiveObjObserverable(): Observable<boolean> {
    return this.active.asObservable();
  }

  subToTabChange(): void { // Check audiomaker status on every tab change
    this.tabService.activeTabObservable().subscribe((e: AsgardTab) => {
      if (e) {
        setTimeout(() => {
          this.updateCursor();
        }, 400);  // small delay to let Asgard construct the tab
        // otherwise there's no iframe body to set the cursor on
      }
    });

    this.settigsService.getSettingsObservable().subscribe((settings: ISettings) => {
      if (settings) {
        this.speed = settings.speed;
      }
    });
  }

  toggleAudioMaker = () => {
    this.toggleAudioMakerFunction();
  }

  toggleAudioMakerFunction(): void {
    if (this.isActive() && this.tabService.activeTab) {
      this.uiStore.setItemUnActive(AsgardFeatureNames.AUDIO_MAKER);
      this.updateOrResetAudiomakerCursor(this.tabService.activeTab.id, CursorAction.reset);
    } else if (this.tabService.activeTab) {
      this.uiStore.setItemActive(AsgardFeatureNames.AUDIO_MAKER);
      this.updateOrResetAudiomakerCursor(this.tabService.activeTab.id, CursorAction.update);
    }
  }

  updateCursor(): void {
    if (this.tabService.activeTab === null) {
      return;
    }

    let action: CursorAction;
    this.isActive() ? action = CursorAction.update : action = CursorAction.reset;
    this.updateOrResetAudiomakerCursor(this.tabService.activeTab.id, action);
  }

  updateOrResetAudiomakerCursor(documentId: string, action: CursorAction): void {
    const pdfDoc: HTMLElement | null = documentElement(documentId);
    const froalaFrameCollection: HTMLCollection | null = getFroalaIframes();

    if (pdfDoc) {
      action === CursorAction.reset ?
        resetCursorIcon(documentId, CustomDocviewerCursorStyles.audiomaker) :
        updateCursorIcon(documentId, CustomDocviewerCursorStyles.audiomaker);
    } else if (froalaFrameCollection && froalaFrameCollection.length > 0) {
      Array.from(froalaFrameCollection).forEach(froalaFrame => {
        const froalaIframe = froalaFrame as HTMLIFrameElement; // explicit cast to avoid linter error
        if (froalaIframe.contentDocument) {
          const froalaBody = froalaIframe.contentDocument.getElementsByClassName('fr-view')[0];

          action === CursorAction.reset ?
            froalaBody.removeAttribute('style') :
            froalaBody.setAttribute('style', AUDIOMAKER_CURSOR_LOC);
        }
      });
    }
  }

  updateCurrentCharacterCount(count: number): void {
    if (count !== this.currentCharacterCount.value) {
      this.currentCharacterCount.next(count);
    }
  }

  getCurrentCharacterCountObservable(): Observable<number> {
    return this.currentCharacterCount.asObservable();
  }

  async getAudioBlob(text: string, voice: string): Promise<Blob> {
    if (AudioMakerService.isEmpty(text)) {
      return Promise.reject(AudioMakerError.emptyText);
    }

    if (this.isBelowNephilimLimit(text)) {
      this.modalWindowService.createModalWindow(SpinnerComponent);
      const mp3Data = await this.getAudioData(text, voice);

      this.modalWindowService.closeSpinner();

      if (AudioMakerService.isValid(mp3Data)) {
        return this.getMP3Blob(mp3Data);
      } else {
        return Promise.reject(AudioMakerError.audioBlobIsInvalid);
      }
    } else {
      return Promise.reject(AudioMakerError.aboveNephilimLimit);
    }
  }

  private isBelowNephilimLimit(text: string): boolean {
    return text.length <= this.nephilimLimit;
  }

  private async getAudioData(text: string, voice: string): Promise<any> {
    const parameters = this.createParameters(text, voice);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob' as 'json'
    };

    return await this.post<any>(this.getAudioMakerUrl(), parameters, httpOptions, RequestTypeEnum.AudioData);

    // return await this.http.post(environment.audioMaker.url, parameters, httpOptions).toPromise() as Promise<any>;
  }

  private getAudioMakerUrl() {
    return currentSpeechEngine() === SpeechEngineVersions.Skidbladnir ? environment.audioMaker.skidbladnirUrl : environment.audioMaker.url;
  }

  private createParameters(text: string, voice: string): IAudioMakerParameters | AudioSettingsV2 {
    if (currentSpeechEngine() === SpeechEngineVersions.Skidbladnir) {
      return {
        text,
        audioSettings: {
          bitrate: 320,
          format: this.audioFormat === AudioFormat.mp3 ? 0 : 1
        },
        ttsParams: {
          rate: this.speed,
          volume: 100,
          voice,
          pause: this.settigsService.settings.wordsReadingPause || 0
        }
      }
    }

    return {
      text,         // The text that gets converted to speech.
      voice,        // The voice used to speak. If string is empty, default voice is used.
      format: this.audioFormat, // The format of audio file (MP3 or WAV). Default value is MP3.
      pause: this.settigsService.settings.wordsReadingPause || 0,   // Time between words (in ms).
      rate: this.speed,      // The rate of speech (-10 to 10). Default value is 0.
      volume: 100,  // The volume of speech. Default value is 100.
      bitRate: 320  // The bit rate of the audio file in kilobits per second (128, 160, 192, 256, 320). Default value is 128 kbps.
    };
  }

  private getMP3Blob(data: any): Blob {
    return new Blob([data], { type: AudioMakerService.getMimeTypeByFormat(this.audioFormat) });
  }

  private showInstructionsIfNeeded(): void {
    if (!this.userReadInstructions) {
      this.modalWindowService.createModalWindow(CommonMessageDialogComponent, {
        messageKey: 'AUDIOMAKERINSDLG.INSTRUCTION',
        hasDoNotShowAgainButton: true
      });
      this.modalWindowService.doNotShowAgainOk.subscribe(() => this.userReadInstructions = true);
    }
  }
}
